'use client';

import { EligibilityResult } from '@dte/otw-azure-functions/src/common/interfaces/api/EligibilityResult';
import type { PremiseDataDocument } from '@dte/otw-azure-functions/src/common/interfaces/containers/premises/PremiseData';
import { getPremiseEligibilityResult } from '@dte/otw-azure-functions/src/common/premises/data/attributes/eligibility/getPremiseEligibilityResult';
import { Tooltip } from '@mantine/core';
import type { PropsWithChildren, ReactNode } from 'react';
import { addClassName } from '../../../../../components/form/addClassName';
import { EligibilityMessages } from '../(premiseId)/eligibility/PremiseEligibility';
import { PremiseLink } from '../(premiseId)/premiseLink/PremiseLink';
import styles from './PremiseMarker.module.scss';

const markerClassName: Record<EligibilityResult, string | undefined> = {
	[EligibilityResult.ElectricActive]: styles['active'],
	[EligibilityResult.DNP]: styles['warning'],
	[EligibilityResult.ElectricInactive]: styles['inactive'],
	[EligibilityResult.GasOnly]: styles['inactive'],
	[EligibilityResult.NoService]: styles['inactive'],
	[EligibilityResult.NotFound]: styles['inactive'],
};

export interface PremiseMarkerProps extends PropsWithChildren {
	premiseData: PremiseDataDocument | undefined;
	size?: 'small' | 'medium';
}

export function PremiseDetailPopup(props: PremiseMarkerProps): ReactNode {
	const premiseData = props.premiseData;

	const eligibility = getPremiseEligibilityResult(premiseData);

	// IF no message, don't add a popup
	if (!eligibility) {
		return props.children;
	}
	const message = EligibilityMessages[eligibility];
	if (!message) {
		return props.children;
	}

	const tooltipClass = addClassName(styles['premiseDetailTooltip'], markerClassName[eligibility]);
	const tooltipArrowClass = addClassName(styles['premiseDetailTooltipArrow'], markerClassName[eligibility]);

	return (
		<Tooltip
			withArrow={true}
			arrowSize={8}
			arrowOffset={16}
			position={'left'}
			zIndex={9999}
			label={message}
			classNames={{ tooltip: tooltipClass, arrow: tooltipArrowClass }}
		>
			<div>{props.children}</div>
		</Tooltip>
	);
}

export function PremiseMarker(props: PremiseMarkerProps): ReactNode {
	const premiseData = props.premiseData;

	if (!premiseData) {
		return null;
	}

	const eligibility = getPremiseEligibilityResult(premiseData);
	let markerClass = styles['marker'];
	if (eligibility) {
		markerClass = addClassName(markerClass, markerClassName[eligibility]);
	}
	if (props.size === 'small') {
		markerClass = addClassName(markerClass, styles['small']);
	}

	return (
		<PremiseDetailPopup {...props}>
			<PremiseLink premiseId={premiseData.premiseId}>
				<div className={markerClass}>{props.children}</div>
			</PremiseLink>
		</PremiseDetailPopup>
	);
}
